import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/sessions';

const C = {
  bg:'#0D1B2A', surface:'#112233', surface2:'#162840',
  border:'rgba(255,255,255,.08)', border2:'rgba(255,255,255,.12)',
  text:'#F0F6FF', text2:'#A8BFD0', text3:'#607080',
  gold:'#C9A84C', goldBg:'rgba(201,168,76,.12)',
  accent:'#3AAF58', accentBg:'rgba(58,175,88,.12)',
  red:'#EF4444', redBg:'rgba(239,68,68,.12)',
  amber:'#F59E0B', amberBg:'rgba(245,158,11,.12)',
  blue:'#3B82F6', blueBg:'rgba(59,130,246,.12)',
  purple:'#8B5CF6', purpleBg:'rgba(139,92,246,.12)',
};

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

export default function WeddingAI() {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [pipelineData, setPipelineData] = useState(null);

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/ai/wedding-context`);
      const d = await r.json();
      setContext(d);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { loadContext(); }, [loadContext]);

  const runAnalysis = async (type, bookingId) => {
    if (!context) return;
    setAnalyzing(true);
    try {
      const r = await fetch(`${API}/ai/wedding-analysis`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ context, type, bookingId })
      });
      const d = await r.json();
      if (type==='profitability') { setProfitability(d); setActiveTab('profitability'); }
      else if (type==='pipeline') { setPipelineData(d); setActiveTab('pipeline'); }
      else { setAnalysis(d); }
    } catch(e) {}
    setAnalyzing(false);
  };

  const askWedding = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(p=>[...p,{role:'user',content:msg}]);
    setChatLoading(true);
    try {
      const r = await fetch(`${API}/ai/chat`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          system: `You are Botanix Wedding AI — expert in wedding planning, event management, and wedding sales for a premium nature resort. You have deep knowledge of Indian weddings, MICE, and hospitality. Current data: ${JSON.stringify({stats:context?.stats, recent_weddings:context?.weddings?.slice(0,3)})}`,
          messages: [...chatMessages.slice(-6), {role:'user',content:msg}],
        })
      });
      const d = await r.json();
      setChatMessages(p=>[...p,{role:'assistant',content:d.content?.[0]?.text||'No response'}]);
    } catch(e) { setChatMessages(p=>[...p,{role:'assistant',content:'Error'}]); }
    setChatLoading(false);
  };

  if (loading) return <div style={{background:C.bg,minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:C.text3}}>Loading Wedding AI...</div>;

  const stats = context?.stats || {};

  return (
    <div style={{background:C.bg,minHeight:'100vh',color:C.text,padding:'24px 28px',fontFamily:'system-ui,sans-serif'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.text}}>💍 Wedding Intelligence AI</div>
          <div style={{fontSize:13,color:C.text3,marginTop:2}}>Profitability · Pipeline · Package optimization · Upsell intelligence</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={loadContext} style={{padding:'9px 14px',background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:13,cursor:'pointer'}}>↻ Refresh</button>
          <button onClick={()=>runAnalysis('pipeline')} disabled={analyzing}
            style={{padding:'9px 16px',background:C.purpleBg,color:C.purple,border:`1px solid ${C.purple}44`,borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',opacity:analyzing?.6:1}}>
            {analyzing?'⏳':'🎯'} Pipeline Analysis
          </button>
          <button onClick={()=>runAnalysis('overview')} disabled={analyzing}
            style={{padding:'9px 16px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',opacity:analyzing?.6:1}}>
            {analyzing?'⏳ Analyzing...':'🧠 Run Analysis'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:20,flexWrap:'wrap'}}>
        {[
          ['overview','💍 Overview'],
          ['bookings','📋 Wedding Bookings'],
          ['profitability','💰 Profitability'],
          ['pipeline','🎯 Pipeline'],
          ['chat','💬 Wedding AI Chat'],
        ].map(([id,label])=>(
          <button key={id} onClick={()=>setActiveTab(id)}
            style={{padding:'8px 16px',background:activeTab===id?C.gold:'transparent',color:activeTab===id?'#1A1A1A':C.text2,
              border:`1px solid ${activeTab===id?C.gold:C.border}`,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>
            {label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab==='overview' && (
        <div>
          {/* KPIs */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
            {[
              {label:'Total Weddings',value:stats.total_weddings||0,icon:'💍',color:C.gold},
              {label:'Total Revenue',value:inr(stats.total_revenue||0),icon:'💰',color:C.accent},
              {label:'Avg Wedding Value',value:inr(stats.avg_wedding_value||0),icon:'📊',color:C.blue},
              {label:'Active Pipeline',value:inr(stats.pipeline_value||0),icon:'🎯',color:C.purple},
            ].map((k,i)=>(
              <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 18px',borderTop:`3px solid ${k.color}`}}>
                <div style={{fontSize:24,marginBottom:8}}>{k.icon}</div>
                <div style={{fontSize:k.value?.toString().length>8?16:22,fontWeight:900,color:k.color}}>{k.value}</div>
                <div style={{fontSize:11,color:C.text3,marginTop:4,textTransform:'uppercase'}}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* AI Insights */}
          {analysis && (
            <div style={{background:C.surface,border:`1px solid ${C.gold}44`,borderRadius:12,padding:20,marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:14}}>🧠 AI Wedding Intelligence</div>
              <div style={{fontSize:13,color:C.text2,marginBottom:16,lineHeight:1.7}}>{analysis.summary}</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16}}>
                {[
                  {label:'Avg Wedding Value',value:inr(analysis.avg_wedding_value||0),color:C.gold},
                  {label:'Revenue Per Pax',value:inr(analysis.revenue_per_pax||0),color:C.accent},
                  {label:'Avg Guest Count',value:`${Math.round(analysis.avg_pax||0)} pax`,color:C.blue},
                ].map((s,i)=>(
                  <div key={i} style={{padding:'12px 16px',background:C.surface2,borderRadius:8,textAlign:'center'}}>
                    <div style={{fontSize:18,fontWeight:900,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:11,color:C.text3,marginTop:4}}>{s.label}</div>
                  </div>
                ))}
              </div>
              {analysis.top_insights?.length>0 && (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.text3,textTransform:'uppercase',marginBottom:8}}>Key Insights</div>
                  {analysis.top_insights.map((insight,i)=>(
                    <div key={i} style={{display:'flex',gap:8,padding:'8px 12px',background:C.surface2,borderRadius:8,marginBottom:6}}>
                      <span style={{color:C.gold,fontSize:14}}>💡</span>
                      <span style={{fontSize:13,color:C.text2}}>{insight}</span>
                    </div>
                  ))}
                </div>
              )}
              {analysis.immediate_actions?.length>0 && (
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:C.text3,textTransform:'uppercase',marginBottom:8}}>Immediate Actions</div>
                  {analysis.immediate_actions.map((action,i)=>(
                    <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.surface2,borderRadius:8,marginBottom:6,borderLeft:`3px solid ${action.priority==='High'?C.red:action.priority==='Medium'?C.amber:C.accent}`}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:C.text}}>{action.action}</div>
                        <div style={{fontSize:11,color:C.gold,marginTop:2}}>{action.impact}</div>
                      </div>
                      <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,
                        background:action.priority==='High'?C.redBg:action.priority==='Medium'?C.amberBg:C.accentBg,
                        color:action.priority==='High'?C.red:action.priority==='Medium'?C.amber:C.accent}}>
                        {action.priority}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Best months */}
          {analysis?.best_months && (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,color:C.gold,marginBottom:10}}>📅 Best Wedding Months</div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {analysis.best_months.map((m,i)=>(
                  <span key={i} style={{padding:'5px 14px',borderRadius:99,background:`${C.gold}22`,color:C.gold,fontSize:13,fontWeight:700,border:`1px solid ${C.gold}44`}}>{m}</span>
                ))}
              </div>
            </div>
          )}

          {!analysis && (
            <div style={{textAlign:'center',padding:40,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:36,marginBottom:12}}>💍</div>
              <div style={{fontSize:15,fontWeight:600,color:C.text2,marginBottom:8}}>Get Wedding Business Intelligence</div>
              <div style={{fontSize:13,color:C.text3,marginBottom:16}}>AI analysis of your wedding revenue, patterns, and opportunities</div>
              <button onClick={()=>runAnalysis('overview')} style={{padding:'10px 24px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                Run Wedding Analysis
              </button>
            </div>
          )}
        </div>
      )}

      {/* BOOKINGS TAB */}
      {activeTab==='bookings' && (
        <div>
          <div style={{marginBottom:12,fontSize:13,color:C.text3}}>Click any booking to run AI profitability analysis</div>
          {(context?.weddings||[]).map(booking=>(
            <div key={booking.id} style={{background:C.surface,border:`1px solid ${selectedBooking?.id===booking.id?C.gold:C.border}`,borderRadius:12,padding:18,marginBottom:10,cursor:'pointer',borderLeft:`4px solid ${C.gold}`}}
              onClick={()=>{setSelectedBooking(booking);runAnalysis('profitability',booking.id);}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                <div>
                  <div style={{fontSize:15,fontWeight:800,color:C.text}}>{booking.guest_name||'Guest'}</div>
                  <div style={{fontSize:12,color:C.text3}}>{booking.booking_code} · {booking.pax_count} pax · {booking.salesperson}</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:18,fontWeight:900,color:C.gold}}>{inr(booking.total_amount)}</div>
                  <div style={{fontSize:11,color:C.text3,marginTop:2}}>
                    {new Date(booking.start_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                    {booking.end_date&&booking.end_date!==booking.start_date&&` → ${new Date(booking.end_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}`}
                  </div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
                <div style={{padding:'8px 12px',background:C.surface2,borderRadius:6,textAlign:'center'}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.accent}}>{inr(booking.advance_paid)}</div>
                  <div style={{fontSize:10,color:C.text3}}>Advance Paid</div>
                </div>
                <div style={{padding:'8px 12px',background:C.surface2,borderRadius:6,textAlign:'center'}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.red}}>{inr(booking.balance_amount)}</div>
                  <div style={{fontSize:10,color:C.text3}}>Balance Due</div>
                </div>
                <div style={{padding:'8px 12px',background:C.surface2,borderRadius:6,textAlign:'center'}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.gold}}>{inr(Math.round(booking.total_amount/booking.pax_count))}</div>
                  <div style={{fontSize:10,color:C.text3}}>Per Pax</div>
                </div>
              </div>
              {selectedBooking?.id===booking.id && analyzing && (
                <div style={{marginTop:10,textAlign:'center',color:C.gold,fontSize:13}}>⏳ Running profitability analysis...</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* PROFITABILITY TAB */}
      {activeTab==='profitability' && (
        <div>
          {!profitability ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:36,marginBottom:12}}>💰</div>
              <div style={{fontSize:14,color:C.text2}}>Select a booking from the Bookings tab to see profitability analysis</div>
            </div>
          ) : (
            <div>
              {/* Score */}
              <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:16,marginBottom:20}}>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:24,textAlign:'center',minWidth:140}}>
                  <div style={{fontSize:11,color:C.text3,textTransform:'uppercase',marginBottom:8}}>Profitability Score</div>
                  <div style={{fontSize:56,fontWeight:900,color:profitability.profitability_score>=70?C.accent:profitability.profitability_score>=50?C.amber:C.red}}>
                    {profitability.profitability_score}
                  </div>
                  <div style={{fontSize:18,fontWeight:700,color:C.gold}}>{profitability.estimated_margin_pct}% margin</div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  {/* Revenue Breakdown */}
                  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.accent,marginBottom:10,textTransform:'uppercase'}}>Revenue Breakdown</div>
                    {profitability.revenue_breakdown && Object.entries(profitability.revenue_breakdown).map(([k,v])=>(
                      <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                        <span style={{color:C.text2,textTransform:'capitalize'}}>{k.replace(/_/g,' ')}</span>
                        <span style={{fontWeight:700,color:k==='estimated_total'?C.gold:C.text}}>{inr(v)}</span>
                      </div>
                    ))}
                  </div>
                  {/* Cost Breakdown */}
                  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.red,marginBottom:10,textTransform:'uppercase'}}>Estimated Costs</div>
                    {profitability.estimated_cost && Object.entries(profitability.estimated_cost).map(([k,v])=>(
                      <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                        <span style={{color:C.text2,textTransform:'capitalize'}}>{k.replace(/_/g,' ')}</span>
                        <span style={{fontWeight:700,color:k.includes('total')?C.red:C.text}}>{inr(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Upsell Opportunities */}
              {profitability.upsell_opportunities?.length>0 && (
                <div style={{background:C.surface,border:`1px solid ${C.gold}44`,borderRadius:12,padding:18,marginBottom:14}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.gold,marginBottom:12}}>⬆️ Upsell Opportunities</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                    {profitability.upsell_opportunities.map((u,i)=>(
                      <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.surface2,borderRadius:8}}>
                        <span style={{fontSize:13,color:C.text}}>{u.item}</span>
                        <span style={{fontWeight:700,color:C.gold,fontSize:14}}>{inr(u.estimated_value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risks & Recommendations */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                {profitability.risks?.length>0 && (
                  <div style={{background:C.surface,border:`1px solid ${C.red}33`,borderRadius:12,padding:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.red,marginBottom:10,textTransform:'uppercase'}}>⚠️ Risk Factors</div>
                    {profitability.risks.map((r,i)=>(
                      <div key={i} style={{display:'flex',gap:8,padding:'6px 0',fontSize:12,color:C.text2,borderBottom:`1px solid ${C.border}`}}>
                        <span style={{color:C.red}}>•</span>{r}
                      </div>
                    ))}
                  </div>
                )}
                {profitability.recommendations?.length>0 && (
                  <div style={{background:C.surface,border:`1px solid ${C.accent}33`,borderRadius:12,padding:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.accent,marginBottom:10,textTransform:'uppercase'}}>✅ Recommendations</div>
                    {profitability.recommendations.map((r,i)=>(
                      <div key={i} style={{display:'flex',gap:8,padding:'6px 0',fontSize:12,color:C.text2,borderBottom:`1px solid ${C.border}`}}>
                        <span style={{color:C.accent}}>•</span>{r}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* PIPELINE TAB */}
      {activeTab==='pipeline' && (
        <div>
          {!pipelineData ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:36,marginBottom:12}}>🎯</div>
              <div style={{fontSize:14,color:C.text2,marginBottom:16}}>Click "Pipeline Analysis" to analyze your wedding sales pipeline</div>
              <button onClick={()=>runAnalysis('pipeline')} style={{padding:'10px 24px',background:C.purple,color:'#fff',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                Run Pipeline Analysis
              </button>
            </div>
          ) : (
            <div>
              {/* Pipeline Health */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:20}}>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                  <div style={{fontSize:11,color:C.text3,textTransform:'uppercase',marginBottom:8}}>Pipeline Health</div>
                  <div style={{fontSize:28,fontWeight:900,color:pipelineData.pipeline_health==='Strong'?C.accent:pipelineData.pipeline_health==='Moderate'?C.amber:C.red}}>
                    {pipelineData.pipeline_health}
                  </div>
                </div>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                  <div style={{fontSize:11,color:C.text3,textTransform:'uppercase',marginBottom:8}}>Total Pipeline</div>
                  <div style={{fontSize:22,fontWeight:900,color:C.gold}}>{inr(pipelineData.total_pipeline_value||0)}</div>
                </div>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                  <div style={{fontSize:11,color:C.text3,textTransform:'uppercase',marginBottom:8}}>Monthly Forecast</div>
                  <div style={{fontSize:22,fontWeight:900,color:C.accent}}>{inr(pipelineData.monthly_forecast?.revenue||0)}</div>
                  <div style={{fontSize:11,color:C.text3}}>{pipelineData.monthly_forecast?.bookings} bookings expected</div>
                </div>
              </div>

              {/* Conversion insights */}
              {pipelineData.conversion_insights && (
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:14}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.gold,marginBottom:8}}>💡 Conversion Intelligence</div>
                  <div style={{fontSize:13,color:C.text2,lineHeight:1.7}}>{pipelineData.conversion_insights}</div>
                </div>
              )}

              {/* Hot Leads */}
              {pipelineData.hot_leads?.length>0 && (
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:14}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.accent,marginBottom:12}}>🔥 Hot Wedding Leads</div>
                  {pipelineData.hot_leads.map((lead,i)=>(
                    <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.surface2,borderRadius:8,marginBottom:6}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:C.text}}>{lead.name}</div>
                        <div style={{fontSize:11,color:C.gold,marginTop:2}}>{lead.action}</div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontSize:14,fontWeight:700,color:C.gold}}>{inr(lead.value)}</div>
                        <div style={{fontSize:11,color:C.accent}}>{lead.probability}% probability</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* At Risk */}
              {pipelineData.at_risk_leads?.length>0 && (
                <div style={{background:C.surface,border:`1px solid ${C.red}33`,borderRadius:12,padding:16,marginBottom:14}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.red,marginBottom:12}}>⚠️ At Risk Leads</div>
                  {pipelineData.at_risk_leads.map((lead,i)=>(
                    <div key={i} style={{padding:'10px 14px',background:C.surface2,borderRadius:8,marginBottom:6}}>
                      <div style={{fontSize:13,fontWeight:700,color:C.text}}>{lead.name}</div>
                      <div style={{fontSize:11,color:C.red,marginTop:2}}>Risk: {lead.reason}</div>
                      <div style={{fontSize:11,color:C.gold,marginTop:2}}>Action: {lead.action}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Top Actions */}
              {pipelineData.top_actions?.length>0 && (
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
                  <div style={{fontSize:13,fontWeight:800,color:C.gold,marginBottom:12}}>✅ Top Actions for Sales Team</div>
                  {pipelineData.top_actions.map((action,i)=>(
                    <div key={i} style={{display:'flex',gap:10,padding:'8px 12px',background:C.surface2,borderRadius:8,marginBottom:6}}>
                      <span style={{color:C.gold,fontWeight:800}}>{i+1}.</span>
                      <span style={{fontSize:13,color:C.text2}}>{action}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CHAT TAB */}
      {activeTab==='chat' && (
        <div>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,marginBottom:12,overflow:'hidden'}}>
            <div style={{maxHeight:420,overflowY:'auto',padding:20}}>
              {chatMessages.length===0 && (
                <div style={{textAlign:'center',padding:40,color:C.text3}}>
                  <div style={{fontSize:32,marginBottom:10}}>💍</div>
                  <div style={{fontSize:14,color:C.text2,marginBottom:16}}>Ask Wedding AI anything</div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',justifyContent:'center'}}>
                    {['What package should I offer a 200 pax wedding?','How to increase wedding revenue?','What is a good price for a Sangeet + Wedding package?','How to handle a client asking for 20% discount?'].map(q=>(
                      <button key={q} onClick={()=>setChatInput(q)} style={{padding:'6px 12px',background:C.surface2,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:11,cursor:'pointer'}}>{q}</button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((m,i)=>(
                <div key={i} style={{marginBottom:14,display:'flex',flexDirection:m.role==='user'?'row-reverse':'row',gap:8}}>
                  <div style={{width:28,height:28,borderRadius:'50%',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',
                    background:m.role==='user'?'#153D4B':`linear-gradient(135deg,${C.gold},#E8C97A)`}}>
                    {m.role==='user'?'👤':'💍'}
                  </div>
                  <div style={{maxWidth:'80%',padding:'10px 14px',borderRadius:10,background:m.role==='user'?'#153D4B':C.surface2,border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:13,lineHeight:1.7,color:C.text,whiteSpace:'pre-wrap'}}>{m.content}</div>
                  </div>
                </div>
              ))}
              {chatLoading && <div style={{textAlign:'center',padding:10,color:C.text3}}>⏳ Wedding AI thinking...</div>}
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <input value={chatInput} onChange={e=>setChatInput(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&askWedding()}
              placeholder="Ask about wedding packages, pricing, planning..."
              style={{flex:1,padding:'12px 16px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:10,color:C.text,fontSize:13,outline:'none'}} />
            <button onClick={askWedding} disabled={chatLoading||!chatInput.trim()}
              style={{padding:'12px 20px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',opacity:chatLoading||!chatInput.trim()?.5:1}}>
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
